import { normalizeMgrs } from './mgrs';

export const SRE_IMAGE_TYPE = 'OVERALL';

export const MAX_SRE_PHOTOS = 10;
export const MAX_CAPTION_LENGTH = 500;
export const MAX_ADDITIONAL_COMMENTS_LENGTH = 4000;
export const MAX_MGRS_LENGTH = 50;
export const MAX_SHORT_TEXT_LENGTH = 50;
export const MAX_REPORTER_TITLE_LENGTH = 255;

/**
 * A photo in an SRE report. Photos already saved have an `id` and `url` is a browser URL for
 * showing them; photos just added have no `id` and `url` is the data URL that will be uploaded.
 */
export interface SrePhoto {
  id?: string;
  url: string;
  caption: string | null;
  displayOrder: number;
}

/** The parts of a saved photo that can change after it was created. */
export interface StoredPhotoMetadata {
  id: string;
  caption?: string | null;
  displayOrder?: number | null;
}

/** A blank caption is stored as null, and anything over the limit is cut off. */
export function normalizeCaption(caption: string | null | undefined): string | null {
  const trimmed = (caption ?? '').trim();
  return trimmed === '' ? null : trimmed.slice(0, MAX_CAPTION_LENGTH);
}

/** Photo displayOrder always matches the position in the list. */
export function renumberPhotos(photos: SrePhoto[]): SrePhoto[] {
  return photos.map((photo, index) => ({ ...photo, displayOrder: index }));
}

/** Adds a photo to the end of the list; a list that is already full is returned unchanged. */
export function addPhoto(photos: SrePhoto[], url: string, caption: string | null | undefined): SrePhoto[] {
  if (photos.length >= MAX_SRE_PHOTOS) {
    return photos;
  }
  return renumberPhotos([...photos, { url, caption: normalizeCaption(caption), displayOrder: photos.length }]);
}

export function removePhoto(photos: SrePhoto[], index: number): SrePhoto[] {
  return renumberPhotos(photos.filter((_, i) => i !== index));
}

export function setPhotoCaption(photos: SrePhoto[], index: number, caption: string | null | undefined): SrePhoto[] {
  return photos.map((photo, i) => (i === index ? { ...photo, caption: normalizeCaption(caption) } : photo));
}

/**
 * Works out what has to be sent to the API so that the stored photos match the photos on screen.
 *
 * @param originals the photos stored for the report when it was opened
 * @param photos the photos on screen (saved ones keep their `id`)
 */
export function planPhotoChanges(
  originals: StoredPhotoMetadata[],
  photos: SrePhoto[],
): {
  creates: { url: string; caption: string | null; displayOrder: number }[];
  updates: { id: string; caption: string | null; displayOrder: number }[];
  deletes: string[];
} {
  const originalsById = new Map(originals.map((original) => [original.id, original]));
  const keptIds = new Set(photos.filter((photo) => photo.id).map((photo) => photo.id as string));

  const creates: { url: string; caption: string | null; displayOrder: number }[] = [];
  const updates: { id: string; caption: string | null; displayOrder: number }[] = [];

  photos.forEach((photo, index) => {
    const caption = normalizeCaption(photo.caption);

    if (!photo.id) {
      creates.push({ url: photo.url, caption, displayOrder: index });
      return;
    }

    const original = originalsById.get(photo.id);
    const changed = !original || (original.caption ?? null) !== caption || original.displayOrder !== index;
    if (changed) {
      updates.push({ id: photo.id, caption, displayOrder: index });
    }
  });

  const deletes = originals.filter((original) => !keptIds.has(original.id)).map((original) => original.id);

  return { creates, updates, deletes };
}

/**
 * Parses a number typed into a text field. Blank is null (not provided), and anything that is
 * not a plain non-negative number is NaN.
 */
export function parseOptionalNumber(text: string | number | null | undefined): number | null {
  if (text === null || text === undefined || String(text).trim() === '') {
    return null;
  }
  const normalized = String(text).trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return NaN;
  }
  return Number(normalized);
}

function blankToNull(text: string | null | undefined): string | null {
  const trimmed = (text ?? '').toString().trim();
  return trimmed === '' ? null : trimmed;
}

/** The details-form values as the fields of an SRE hero report; blanks become null. */
export function sreDetailsFromForm(values: {
  mgrs?: string | null;
  depthCm?: string | number | null;
  parcelNumber?: string | null;
  laneNumber?: string | null;
  distance?: string | number | null;
  pocName?: string | null;
  reporterTitle?: string | null;
  firstNoticedDate?: Date | null;
  additionalComments?: string | null;
}) {
  return {
    // A valid MGRS is saved cleaned up: upper case, no spaces.
    mgrs: blankToNull(values.mgrs) === null ? null : normalizeMgrs(values.mgrs),
    depthCm: parseOptionalNumber(values.depthCm),
    parcelNumber: blankToNull(values.parcelNumber),
    laneNumber: blankToNull(values.laneNumber),
    distance: parseOptionalNumber(values.distance),
    pocName: blankToNull(values.pocName),
    reporterTitle: blankToNull(values.reporterTitle),
    firstNoticedDate: values.firstNoticedDate ?? null,
    additionalComments: blankToNull(values.additionalComments),
  };
}

/**
 * Whether a report is shown by its MGRS rather than by latitude/longitude: a report, SRE or UXO, that has an
 * MGRS. Reports saved without one (UXO reports from before they had an MGRS) keep showing coordinates.
 */
export function showsMgrs(report: { mgrs?: string | null } | null | undefined): boolean {
  return (report?.mgrs ?? '').trim() !== '';
}
